import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import homebanner from "@assets/images/homeBanner.jpg";
import areaImg from "@assets/images/Downtown_Dubai.webp";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaFilter,
  FaSort,
  FaTimes,
  FaSearch,
  FaBuilding,
  FaHome,
} from "react-icons/fa";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEO from "../../../utils/SeoComponent";
import { ROUTES } from "../../../constants/routes";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${homebanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const FilterSection = styled(Box)(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: "12px",
  padding: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
  marginBottom: theme.spacing(6),
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15)",
  },
}));

const ProjectCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 240,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "50%",
    background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
  },
}));

const ComingSoonTag = styled(Typography)(({ theme }) => ({
  display: "inline-block",
  background: "#C10E14",
  color: "white",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: "4px",
  fontWeight: 600,
  fontSize: "0.75rem",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const LocationWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.5),
  fontSize: "0.9rem",
}));

const TypeButton = styled(Box)(({ theme }) => ({
  display: "inline-block",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: "50px",
  fontSize: "0.8rem",
  fontWeight: 600,
  backgroundColor: "#f0f4f8",
  color: "#C10E14",
  textTransform: "capitalize",
}));

const FilterButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: "50px",
  textTransform: "capitalize",
  padding: theme.spacing(0.8, 2),
  fontWeight: 600,
  borderColor: "#e0e0e0",
  "&.MuiButton-contained": {
    backgroundColor: "#C10E14",
    color: "white",
    "&:hover": {
      backgroundColor: "#A30B11",
    },
  },
  "&.MuiButton-outlined": {
    color: "#333",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(10),
}));

const PaginationButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  margin: theme.spacing(0, 1),
  padding: theme.spacing(1, 3),
  fontWeight: 600,
}));

// Create a global cache outside of component scope
const globalProjectCache = {};

const Project = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const isMounted = useRef(true);
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    property_types: [],
  });
  const [selectedFilters, setSelectedFilters] = useState({
    city_id: [],
    property_type: [],
    property_name: "",
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query");

  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const getProjectTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "apartment":
        return <FaBuilding />;
      case "villa":
        return <FaHome />;
      default:
        return <FaBuilding />;
    }
  };

  const fetchProjects = useCallback(
    (page = 1) => {
      // Check global cache first
      if (globalProjectCache[page] && !initialLoad) {
        setProjects(globalProjectCache[page]);
        setCurrentPage(page);
        setLoading(false);
        return;
      }

      setLoading(true);

      axios
        .post(
          `https://panel.estaty.app/api/v1/getProperties?page=${page}`,
          {},
          {
            headers: {
              "App-key": "3dc9efc076a8d36602ef445c924fa7cf",
            },
          }
        )
        .then((response) => {
          if (response.data && isMounted.current) {
            const fetchedProjects = response.data?.properties?.data || [];
            setProjects(fetchedProjects);
            setCurrentPage(response.data.properties.current_page);
            setLastPage(response.data.properties.last_page);

            // Update global cache
            globalProjectCache[page] = fetchedProjects;
            setInitialLoad(false);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching projects:", error);
          setLoading(false);
        });
    },
    [initialLoad]
  );

  const fetchFilters = useCallback(() => {
    axios
      .post(
        "https://panel.estaty.app/api/v1/getFilters",
        {},
        {
          headers: {
            "App-key": "3dc9efc076a8d36602ef445c924fa7cf",
          },
        }
      )
      .then((response) => {
        if (response.data && isMounted.current) {
          setFilterOptions({
            cities: response.data.cities || [],
            property_types: response.data.property_types || [],
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching filters:", error);
      });
  }, []);

  const applyFilters = useCallback(() => {
    setFilterLoading(true);

    // Prepare filter parameters
    const filterParams = {};

    // Add name filter if provided
    if (selectedFilters.property_name) {
      filterParams.property_name = selectedFilters.property_name;
    }

    // Add city filter if selected
    if (selectedFilters.city_id.length > 0) {
      filterParams["city_id[]"] = selectedFilters.city_id;
    }

    // Add property type filter if selected
    if (selectedFilters.property_type.length > 0) {
      filterParams["property_type[]"] = selectedFilters.property_type;
    }

    axios
      .post("https://panel.estaty.app/api/v1/filter", filterParams, {
        headers: {
          "App-key": "3dc9efc076a8d36602ef445c924fa7cf",
        },
      })
      .then((response) => {
        if (response.data && isMounted.current) {
          const filteredProjects = response.data?.properties?.data || [];
          setProjects(filteredProjects);
          setCurrentPage(response.data.properties.current_page || 1);
          setLastPage(response.data.properties.last_page || 1);
        }
        setFilterLoading(false);
      })
      .catch((error) => {
        console.error("Error applying filters:", error);
        setFilterLoading(false);
      });
  }, [selectedFilters]);

  const resetFilters = useCallback(() => {
    setSelectedFilters({
      city_id: [],
      property_type: [],
      property_name: "",
    });
    fetchProjects(1);
  }, [fetchProjects]);

  const handleFilterChange = useCallback((filterType, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Update active filters count whenever selectedFilters changes
  useEffect(() => {
    let count = 0;

    if (selectedFilters.property_name) count += 1;
    if (selectedFilters.city_id.length > 0) count += 1;
    if (selectedFilters.property_type.length > 0) count += 1;

    setActiveFiltersCount(count);
  }, [selectedFilters]);

  useEffect(() => {
    isMounted.current = true;
    fetchFilters();

    if (searchQuery) {
      // If there's a search query from the home page
      setSelectedFilters((prev) => ({
        ...prev,
        property_name: searchQuery,
      }));
    } else {
      fetchProjects();
    }

    return () => {
      isMounted.current = false;
    };
  }, [searchQuery, fetchFilters, fetchProjects]);

  useEffect(() => {
    if (searchQuery && isMounted.current) {
      applyFilters();
    }
  }, [searchQuery, applyFilters]);

  useEffect(() => {
    if (isMounted.current) {
      fetchProjects(currentPage);
    }
  }, [currentPage, fetchProjects]);

  return (
    <>
      <SEO slug={"projects"} />
      <Box className="projects-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Projects in Dubai
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Discover premium real estate developments in Dubai's most
                sought-after locations
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("filter-section");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14",
                  "&:hover": {
                    backgroundColor: "#A30B11",
                  },
                }}
              >
                Find Your Property
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Filter Section */}
        <Container
          sx={{ mt: { xs: -6, md: -10 }, position: "relative", zIndex: 2 }}
          id="filter-section"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FilterSection>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Filter Projects
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find your ideal property in Dubai
                  </Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {/* Search by project name */}
                    <TextField
                      placeholder="Search by project name"
                      variant="outlined"
                      size="small"
                      value={selectedFilters.property_name}
                      onChange={(e) =>
                        handleFilterChange("property_name", e.target.value)
                      }
                      sx={{
                        mr: 1,
                        mb: { xs: 1, md: 0 },
                        width: { xs: "100%", sm: "auto" },
                        flexGrow: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          pr: 0.5,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (selectedFilters.property_name) {
                                applyFilters();
                              }
                            }}
                          >
                            <FaSearch />
                          </IconButton>
                        ),
                      }}
                    />

                    {/* Property type filter */}
                    <FormControl
                      size="small"
                      sx={{
                        mr: 1,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: 120,
                        mb: { xs: 1, md: 0 },
                      }}
                    >
                      <Select
                        displayEmpty
                        value={
                          selectedFilters.property_type.length === 1
                            ? selectedFilters.property_type[0]
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            handleFilterChange("property_type", [value]);
                            applyFilters();
                          } else {
                            handleFilterChange("property_type", []);
                          }
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return "Property Type";
                          }
                          const selectedType =
                            filterOptions.property_types.find(
                              (type) => type.id === selected
                            );
                          return selectedType
                            ? selectedType.name
                            : "Property Type";
                        }}
                        sx={{
                          borderRadius: "50px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>All Types</em>
                        </MenuItem>
                        {filterOptions.property_types.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* City filter */}
                    <FormControl
                      size="small"
                      sx={{
                        mr: 1,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: 120,
                        mb: { xs: 1, md: 0 },
                      }}
                    >
                      <Select
                        displayEmpty
                        value={
                          selectedFilters.city_id.length === 1
                            ? selectedFilters.city_id[0]
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            handleFilterChange("city_id", [value]);
                            applyFilters();
                          } else {
                            handleFilterChange("city_id", []);
                          }
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return "City";
                          }
                          const selectedCity = filterOptions.cities.find(
                            (city) => city.id === selected
                          );
                          return selectedCity ? selectedCity.name : "City";
                        }}
                        sx={{
                          borderRadius: "50px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>All Cities</em>
                        </MenuItem>
                        {filterOptions.cities.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Apply filters button */}
                    <FilterButton
                      variant="contained"
                      onClick={applyFilters}
                      sx={{ ml: { xs: 0, md: 1 } }}
                    >
                      Apply Filters
                    </FilterButton>

                    {/* Reset filters button (only show if filters are active) */}
                    {activeFiltersCount > 0 && (
                      <Button
                        variant="text"
                        onClick={resetFilters}
                        startIcon={<FaTimes />}
                        sx={{
                          ml: 1,
                          color: "#666",
                          textTransform: "none",
                          fontSize: "0.85rem",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#C10E14",
                          },
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </FilterSection>
          </motion.div>
        </Container>

        {/* Projects List Section */}
        <PageSection id="projects-list" className="projects-list-section">
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                {filterLoading ? "Searching Projects..." : "New Projects"}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                Explore the latest development projects in Dubai's most
                prestigious locations
              </Typography>

              {/* Active filters display */}
              {activeFiltersCount > 0 && (
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {selectedFilters.property_name && (
                    <Chip
                      label={`Name: ${selectedFilters.property_name}`}
                      onDelete={() => handleFilterChange("property_name", "")}
                      color="primary"
                      sx={{
                        backgroundColor: "#C10E14",
                        "& .MuiChip-deleteIcon": { color: "white" },
                      }}
                    />
                  )}

                  {selectedFilters.city_id.length > 0 &&
                    selectedFilters.city_id.map((cityId) => {
                      const city = filterOptions.cities.find(
                        (c) => c.id === cityId
                      );
                      return (
                        <Chip
                          key={cityId}
                          label={`City: ${city ? city.name : cityId}`}
                          onDelete={() => {
                            handleFilterChange(
                              "city_id",
                              selectedFilters.city_id.filter(
                                (id) => id !== cityId
                              )
                            );
                            applyFilters();
                          }}
                          color="primary"
                          sx={{
                            backgroundColor: "#C10E14",
                            "& .MuiChip-deleteIcon": { color: "white" },
                          }}
                        />
                      );
                    })}

                  {selectedFilters.property_type.length > 0 &&
                    selectedFilters.property_type.map((typeId) => {
                      const type = filterOptions.property_types.find(
                        (t) => t.id === typeId
                      );
                      return (
                        <Chip
                          key={typeId}
                          label={`Type: ${type ? type.name : typeId}`}
                          onDelete={() => {
                            handleFilterChange(
                              "property_type",
                              selectedFilters.property_type.filter(
                                (id) => id !== typeId
                              )
                            );
                            applyFilters();
                          }}
                          color="primary"
                          sx={{
                            backgroundColor: "#C10E14",
                            "& .MuiChip-deleteIcon": { color: "white" },
                          }}
                        />
                      );
                    })}
                </Box>
              )}
            </Box>

            {loading || filterLoading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6">Loading projects...</Typography>
              </Box>
            ) : projects.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6">
                  No projects found matching your criteria
                </Typography>
                <Button
                  variant="contained"
                  onClick={resetFilters}
                  sx={{
                    mt: 2,
                    borderRadius: "50px",
                    backgroundColor: "#C10E14",
                    "&:hover": {
                      backgroundColor: "#A30B11",
                    },
                  }}
                >
                  Reset Filters
                </Button>
              </Box>
            ) : (
              <Grid container spacing={4}>
                {projects.map((project, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <ProjectCard
                        onClick={() =>
                          navigate(ROUTES.PROJECT_DETAIL(project.id))
                        }
                      >
                        <CardActionArea>
                          <Box sx={{ position: "relative" }}>
                            <ProjectCardMedia
                              image={project.cover || areaImg}
                              title={project.title}
                              alt={project.title}
                            />
                          </Box>
                          <CardContent>
                            <Typography
                              variant="h6"
                              component="h3"
                              fontWeight={600}
                              gutterBottom
                            >
                              {project.title}
                            </Typography>
                            <ComingSoonTag>
                              {project?.sales_status?.name}
                            </ComingSoonTag>
                            <LocationWrapper>
                              <FaMapMarkerAlt style={{ marginRight: 8 }} />
                              {project.city ? project.city.name : "Dubai"}
                            </LocationWrapper>
                            <TypeButton>
                              {getProjectTypeIcon(project.property_type?.name)}
                              <Box component="span" sx={{ ml: 1 }}>
                                {project.property_type?.name || "Property"}
                              </Box>
                            </TypeButton>
                          </CardContent>
                        </CardActionArea>
                      </ProjectCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        </PageSection>

        {/* Pagination */}
        {!filterLoading && projects.length > 0 && (
          <PaginationContainer>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <PaginationButton
                onClick={() => {
                  if (currentPage > 1) {
                    fetchProjects(currentPage - 1);
                  }
                }}
                disabled={currentPage === 1}
                sx={{
                  backgroundColor: "#C10E14",
                  "&:hover": { backgroundColor: "#A30B11" },
                  color: "white",
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >
                Previous
              </PaginationButton>

              <PaginationButton
                onClick={() => {
                  if (currentPage < lastPage) {
                    fetchProjects(currentPage + 1);
                  }
                }}
                disabled={currentPage === lastPage}
                sx={{
                  backgroundColor: "#C10E14",
                  "&:hover": { backgroundColor: "#A30B11" },
                  color: "white",
                  opacity: currentPage === lastPage ? 0.5 : 1,
                }}
              >
                Next
              </PaginationButton>
            </motion.div>
          </PaginationContainer>
        )}
      </Box>
    </>
  );
};

export default Project;
